/**
 * The people who can sign in: organization-wide, and every action immediate.
 *
 * NOT A DRAFT, AND THE ONE PANE THE SAVE BAR NEVER COVERS. Adding somebody sends them an
 * email, and "remove access" is a door that should shut when it is pressed, not when somebody
 * later remembers the bar. Settings v2 drew members a read-only list with "message our team";
 * Illia kept self-serve (2026-09-26), so every member can add, send again, remove and give
 * access back, exactly as the organization-access work built it. There is no separate
 * internal "Team access" pane.
 *
 * AN INTERNAL READER NEEDS AN AGENT SELECTED BEFORE THE ADD ROW APPEARS: the Worker reads a
 * member's organization from their session, but ours has to be named, from the agent on
 * screen, and offering the form with nothing selected would only produce a 400.
 *
 * NAMES ARE WHAT THE TEAM HAS, WHICH IS MOSTLY NONE. People are added by email, so the name
 * column falls back to the email's local part, and the email column carries the address.
 */

import { useState } from "react";
import { ApiError, post, type SettingsPage } from "../../../api";
import { Avatar, initials } from "../../Shell";
import { Basis, Button, SettingCard, TextInput, cx } from "../../../ui";
import { when } from "../../../format";

/** The row actions: plain words, one weight. */
const ACTION = "cursor-pointer text-sm font-medium text-ink-soft hover:text-ink";

export function TeamPane({ data, me, onChanged }: { data: SettingsPage; me: string; onChanged: () => void }) {
  const internal = data.user.role === "internal";
  const canEdit = internal ? Boolean(data.agents) && Boolean(data.agent) : true;
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  function told(address: string, sent: { emailed: boolean; signInAt: string }) {
    return sent.emailed ? `We emailed ${address} a link to the sign-in page.` : `We could not email ${address}. Tell them to sign in at ${sent.signInAt}`;
  }

  async function act(run: () => Promise<void>, fallback: string) {
    setError(null);
    setNotice(null);
    try {
      await run();
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : fallback);
    }
  }

  async function add() {
    const address = email.trim();
    if (!address) return setError("Enter an email address");
    await act(async () => {
      const sent = await post<{ emailed: boolean; signInAt: string }>(
        "/v1/settings/users",
        internal ? { email: address, organizationId: data.agent.organizationId } : { email: address },
      );
      setEmail("");
      setNotice(told(address, sent));
    }, "Could not add them");
  }

  const cols = canEdit ? "grid-cols-[minmax(0,1fr)_minmax(0,1fr)_170px_200px]" : "grid-cols-[minmax(0,1fr)_minmax(0,1fr)_170px]";

  return (
    <>
      <SettingCard flush>
        <div className={cx("grid gap-4 border-b border-line-soft bg-ground px-5 py-2.5 text-2xs font-semibold text-ink-faint max-[720px]:hidden", cols)}>
          <span>Name</span>
          <span>Email</span>
          <span>Last signed in</span>
          {canEdit && <span />}
        </div>
        {data.team.length === 0 && <p className="px-5 py-6 text-sm text-ink-faint">Nobody can sign in to this organization yet.</p>}
        {data.team.map((member, index) => (
          <div
            key={member.id}
            className={cx(
              "grid items-center gap-4 px-5 py-3",
              cols,
              index > 0 && "border-t border-line-soft",
              member.disabledAt !== null && "opacity-55",
              "max-[720px]:grid-cols-1 max-[720px]:gap-1",
            )}
          >
            <span className="flex min-w-0 items-center gap-2.5">
              <Avatar className="size-7 bg-sunken text-ink">{initials(member.name ?? member.email)}</Avatar>
              <span className="truncate text-base font-medium text-ink">{member.name ?? member.email.split("@")[0]}</span>
              {member.email === me && <Basis inline>you</Basis>}
              {member.disabledAt && <span className="rounded-full bg-line-soft px-2 py-px text-2xs font-semibold text-ink-faint">Access removed</span>}
            </span>
            <span className="truncate text-sm text-ink-soft">{member.email}</span>
            <span className="nums text-xs text-ink-soft">
              {member.disabledAt ? `Removed ${when(member.disabledAt)}` : member.lastLoginAt ? when(member.lastLoginAt) : "Hasn't signed in yet"}
            </span>
            {canEdit && (
              <span className="flex justify-end gap-4 max-[720px]:justify-start">
                {member.email !== me && (
                  <>
                    {/* Only before their first sign-in: after it they know the way. */}
                    {!member.disabledAt && !member.lastLoginAt && (
                      <button
                        type="button"
                        className={ACTION}
                        onClick={() =>
                          act(async () => {
                            const sent = await post<{ emailed: boolean; signInAt: string }>(`/v1/settings/users/${member.id}/remind`, {});
                            setNotice(told(member.email, sent));
                          }, "Could not send it")
                        }
                      >
                        Send again
                      </button>
                    )}
                    <button
                      type="button"
                      className={ACTION}
                      onClick={() => act(async () => void (await post(`/v1/settings/users/${member.id}/disable`, { disabled: !member.disabledAt })), "Could not change their access")}
                    >
                      {/* BOTH HALVES. Removing somebody is not a one-way door. */}
                      {member.disabledAt ? "Give access back" : "Remove access"}
                    </button>
                  </>
                )}
              </span>
            )}
          </div>
        ))}
        {canEdit && (
          <div className="flex flex-wrap items-center gap-2 border-t border-line-soft bg-ground px-5 py-3">
            <TextInput
              type="email"
              className="min-w-0 flex-1"
              value={email}
              placeholder="name@company.com"
              aria-label="Their email address"
              onChange={(e) => {
                setEmail(e.target.value);
                setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                e.preventDefault();
                void add();
              }}
            />
            <Button type="button" variant="primary" onClick={() => void add()}>
              Add person
            </Button>
            {error && <span className="basis-full text-xs text-alert">{error}</span>}
            {notice && <span className="basis-full text-xs text-ink-soft">{notice}</span>}
          </div>
        )}
      </SettingCard>
      <p className="text-xs text-ink-faint">
        {canEdit
          ? "Everybody here can add or remove people. When you add somebody we email them where to sign in. These changes happen straight away, not with Save."
          : "Choose an agent to add people to its organization."}
      </p>
    </>
  );
}
